import { redirectToLoginIfRequired } from '../utils/common';
import { fetchRetryWithAuth } from '../utils/fetchRetry';
import Logger from '../utils/logger';
import { getMarketSdk } from '../utils/sdk';
import types from './_actionTypes';

const API_URL = process.env.REACT_APP_INSIGHT_API_DOMAIN;

function getUserScanUrl(userId, scanId = '') {
  return `https://${API_URL}/user/${userId}/scans/${scanId}`;
}

function getLastScanUrl(userId) {
  return `https://${API_URL}/user/${userId}/scans/last-scan`;
}

function initializeSdk(user) {
  if (!user || !user.id || user.tokenIsValid !== true || !user.token) {
    Logger.error({
      errorCode: 'E0022',
      message: 'Invalid Token'
    });
    return null;
  }

  return getMarketSdk(user.token);
}

function receiveScanResponse(scanName, response) {
  return {
    type: types.RECEIVE_SCAN,
    name: scanName,
    data: response
  };
}

function receiveUserScansResponse(response) {
  return {
    type: types.RECEIVE_USER_SCANS,
    data: response
  };
}

function receiveLastScanResponse(response) {
  return {
    type: types.RECEIVE_LAST_SCAN,
    data: response
  };
}

function saveLastScanResponse(scan) {
  return {
    type: types.SAVE_LAST_SCAN,
    data: scan
  };
}

function saveScanResponse(scan) {
  return {
    type: types.SAVE_SCAN,
    data: scan
  };
}

function overwriteScanResponse(scan) {
  return {
    type: types.OVERWRITE_SCAN,
    data: scan
  };
}

function deleteScanResponse(response, scanId) {
  return {
    type: types.DELETE_SCAN,
    id: scanId,
    data: response
  };
}

export function errorRequestingScan(scanName, error) {
  return {
    type: types.ERROR_REQUESTING_SCAN,
    name: scanName,
    error: error,
    errorCode: 'E0018'
  };
}

export function errorRequestingUserScans(error) {
  return {
    type: types.ERROR_REQUESTING_USER_SCANS,
    error: error,
    errorCode: 'E0032'
  };
}

export function errorRequestingLastScan(error) {
  return {
    type: types.ERROR_REQUESTING_LAST_SCAN,
    error: error,
    errorCode: 'E0033'
  };
}

export function errorSavingLastScan(lastScan, error) {
  return {
    type: types.ERROR_SAVING_LAST_SCAN,
    lastScan: lastScan,
    error: error,
    errorCode: 'E0034'
  };
}

export function errorSavingScan(scan, error) {
  return {
    type: types.ERROR_SAVING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0035'
  };
}

export function errorOverwritingScan(scan, error) {
  return {
    type: types.ERROR_OVERWRITING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0036'
  };
}

export function errorDeletingScan(scanId, error) {
  return {
    type: types.ERROR_DELETING_SCAN,
    scanId: scanId,
    error: error,
    errorCode: 'E0037'
  };
}

/**
 * Fetches scan results using the insight-market-sdk
 * @param {string} scanName - The name of the scan to fetch
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchScan('SecurityType'))
 */
export function fetchScan(scanName) {
  return (dispatch, getState) => {
    const sdk = initializeSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingScan(
          scanName,
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans.getScanResults(scanName)
      .then((response) => {
        // Validate SDK response structure
        if (!response) {
          throw new Error(`SDK returned null/undefined for scan: ${scanName}`);
        }

        // The SDK response is directly an object of symbol names/IDs
        // Structure: { [symbolId]: { filterValue: string, displayValues: {...} } }
        if (typeof response !== 'object' || Array.isArray(response)) {
          throw new Error(
            `SDK response must be an object with symbol IDs as keys. Received: ${JSON.stringify(
              response
            )}`
          );
        }

        // Transform to expected format: { data: { symbols: response }, ok: true }
        // The response itself is the symbols object
        const transformedResponse = {
          data: {
            symbols: response
          },
          ok: true
        };

        return dispatch(receiveScanResponse(scanName, transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingScan(scanName, ex));
      });
  };
}

export function fetchUserScans() {
  let req = {
    method: 'get'
  };

  return fetchRetryWithAuth(
    req,
    getUserScanUrl,
    receiveUserScansResponse,
    errorRequestingUserScans
  );
}

export function fetchLastScan() {
  let req = {
    method: 'get'
  };

  return fetchRetryWithAuth(
    req,
    getLastScanUrl,
    receiveLastScanResponse,
    errorRequestingLastScan
  );
}

export function saveLastScan(lastScan) {
  let req = {
    method: 'put',
    body: JSON.stringify(lastScan)
  };

  return fetchRetryWithAuth(
    req,
    getLastScanUrl,
    saveLastScanResponse,
    (error) => {
      return errorSavingLastScan(lastScan, error);
    }
  );
}

export function saveScan(newScan, handleSuccess, handleFail) {
  let req = {
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(newScan)
  };

  return fetchRetryWithAuth(
    req,
    getUserScanUrl,
    (scan) => {
      // This is done this way so that the scan container
      // can get the ID (which is generated server side)
      // for the newly created scan so it can be loaded.
      handleSuccess(scan.data.scan.id);
      return saveScanResponse(scan);
    },
    (error) => {
      return errorSavingScan(newScan, error);
    },
    '',
    null,
    handleFail
  );
}

export function overwriteScan(existingScan, handleSuccess, handleFail) {
  let req = {
    method: 'put',
    headers: {},
    body: JSON.stringify(existingScan)
  };

  return fetchRetryWithAuth(
    req,
    (userId) => {
      return getUserScanUrl(userId, existingScan.id);
    },
    overwriteScanResponse,
    (error) => {
      return errorOverwritingScan(existingScan, error);
    },
    '',
    handleSuccess,
    handleFail
  );
}

export function deleteScan(scanId, handleSuccess, handleFail) {
  let req = {
    method: 'delete',
    headers: {}
  };

  return fetchRetryWithAuth(
    req,
    (userId) => {
      return getUserScanUrl(userId, scanId);
    },
    (response) => {
      return deleteScanResponse(response, scanId);
    },
    (error) => {
      return errorDeletingScan(scanId, error);
    },
    '',
    handleSuccess,
    handleFail
  );
}
